// --- React
import { useCallback, useMemo, useState } from "react";
// --- Config & Utils
import * as columnDefs from "../../../../../components/AGGrid/config/columns-def";
// --- Hooks
import { usePMXPricesAndDatesData } from "../../../hooks/usePMXPricesAndDatesData";
import { normalizeSpringDataPage } from "tap/utils/pagination.js";

/**
 * Custom hook for PMX Prices and Dates grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the PMX prices and dates grid
 * - Fetches sorted PMX prices and dates data from the API
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Optional array of column handler functions
 * @returns {object} { setSort, columns }
 */
export function usePMXPricesAndDatesGridUtils({ columnHandlers = [] } = {}) {
  const {
    fetchPMXPricesAndDates,
    updatePMXPricesAndDates,
    pmxPricesAndDatesRange,
    itemsPerPage,
  } = usePMXPricesAndDatesData();

  const [sortedColId, setSortedColId] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle sorting: update state, fetch sorted data, update PMX prices and dates
  const setSort = useCallback(
    async (colId, direction) => {
      setSortedColId(colId);
      setSortDirection(direction);

      const customSort = [colId, direction.toUpperCase()];
      const data = await fetchPMXPricesAndDates(
        pmxPricesAndDatesRange,
        null,
        customSort,
      );

      if (data) {
        const meta = normalizeSpringDataPage(data, itemsPerPage);
        updatePMXPricesAndDates({
          pricesAndDates: data?.content,
          totalPages: meta?.totalPages ?? data?.totalPages,
          totalItems: meta?.totalElements ?? data?.totalElements,
          currentPage: meta?.currentPage ?? data?.pageable?.pageNumber ?? 0,
          pmxPricesAndDatesSort: customSort,
        });
      }
    },
    [
      fetchPMXPricesAndDates,
      updatePMXPricesAndDates,
      pmxPricesAndDatesRange,
      itemsPerPage,
    ],
  );

  // Prepare column definitions with sorting controls
  const baseColumns = columnDefs["pmxPricesAndDates"](...columnHandlers)[
    "default"
  ];

  const columns = useMemo(
    () =>
      baseColumns.map((col) => ({
        ...col,
        headerComponentParams: {
          ...col.headerComponentParams,
          setSort,
          sortedColId,
          sortDirection,
        },
      })),
    [setSort, sortedColId, sortDirection, baseColumns],
  );

  return { setSort, columns };
}
